import { useState } from 'react';
import { Sparkles, X, RotateCcw } from 'lucide-react';

interface AIResultsReviewProps {
  imageData: string;
  aiResults: {
    name: string;
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    confidence?: number;
  };
  onSave: (foodData: {
    name: string;
    calories: string;
    protein: string;
    carbs: string;
    fat: string;
    imageData: string;
  }) => void;
  onRetake: () => void;
  onClose: () => void;
  isLoading?: boolean;
}

export default function AIResultsReview({
  imageData,
  aiResults,
  onSave,
  onRetake,
  onClose,
  isLoading = false
}: AIResultsReviewProps) {
  const [foodName, setFoodName] = useState(aiResults.name);
  const [calories, setCalories] = useState(aiResults.calories.toString());
  const [protein, setProtein] = useState(aiResults.protein.toString());
  const [carbs, setCarbs] = useState(aiResults.carbs.toString());
  const [fat, setFat] = useState(aiResults.fat.toString());

  const handleSubmit = () => {
    if (!foodName.trim()) return;

    onSave({
      name: foodName,
      calories,
      protein,
      carbs,
      fat,
      imageData,
    });
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-3xl p-8 max-w-sm mx-4 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Analyzing Food...</h3>
          <p className="text-sm text-gray-500">AI is identifying nutrition information</p>
          <div className="mt-4 flex justify-center gap-1">
            <div className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 flex flex-col">
      <div className="bg-white border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <X size={20} strokeWidth={2.5} />
          </button>

          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-600" />
            <h2 className="text-lg font-bold text-gray-900">AI Results</h2>
          </div>

          <button
            onClick={onRetake}
            className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <RotateCcw size={18} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="bg-white rounded-2xl overflow-hidden border border-gray-200">
          <img
            src={imageData}
            alt="Captured food"
            className="w-full h-48 object-cover"
          />
        </div>

        {aiResults.confidence && (
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-4 border border-cyan-200">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-cyan-600" />
              <span className="text-sm font-semibold text-cyan-900">AI Confidence</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 bg-white rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full transition-all"
                  style={{ width: `${aiResults.confidence}%` }}
                ></div>
              </div>
              <span className="text-sm font-bold text-cyan-900">{aiResults.confidence}%</span>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl p-5 border border-gray-200 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Food Name</label>
            <input
              type="text"
              value={foodName}
              onChange={(e) => setFoodName(e.target.value)}
              placeholder="e.g., Caesar Salad"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Calories</label>
              <input
                type="number"
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Protein (g)</label>
              <input
                type="number"
                value={protein}
                onChange={(e) => setProtein(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Carbs (g)</label>
              <input
                type="number"
                value={carbs}
                onChange={(e) => setCarbs(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Fat (g)</label>
              <input
                type="number"
                value={fat}
                onChange={(e) => setFat(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-600">
              The AI has analyzed your food image. You can edit any values before saving to ensure accuracy.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border-t border-gray-200 p-4">
        <div className="flex gap-3">
          <button
            onClick={onRetake}
            className="flex-1 px-6 py-3 border border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Retake Photo
          </button>
          <button
            onClick={handleSubmit}
            disabled={!foodName.trim()}
            className="flex-1 px-6 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Food
          </button>
        </div>
      </div>
    </div>
  );
}
